import { motion } from "framer-motion";
import {
  Globe2,
  Briefcase,
  ShieldCheck,
  Cloud,
  AlertOctagon,
  Layers,
  CheckCircle2,
} from "lucide-react";

/* ---------------- Capability cards ---------------- */
const CAPABILITIES = [
  {
    code: "CAP-01",
    icon: Globe2,
    title: "Worldwide Remote Delivery",
    sub: "4 continents · 14 time zones",
    desc:
      "Live cohorts and advisory sessions delivered globally across IST / GMT / CET / EST overlap windows.",
    accent: "sky",
    stat: { label: "Time-zone reach", values: [0.6, 0.85, 0.95, 0.75, 0.6, 0.5] },
  },
  {
    code: "CAP-02",
    icon: Briefcase,
    title: "Enterprise Consulting Capability",
    sub: "Fortune 500 & regulated sectors",
    desc:
      "Strategic security & cloud advisory for procurement-led engagements — NDA-friendly, governance aware.",
    accent: "blue",
    stat: { label: "Enterprise engagements", values: [0.3, 0.45, 0.55, 0.7, 0.82, 0.95] },
  },
  {
    code: "CAP-03",
    icon: ShieldCheck,
    title: "Practical SOC Workshops",
    sub: "Sentinel · Defender XDR · Splunk",
    desc:
      "Detection-engineering drills using real-world telemetry — KQL hunting, alert tuning, IR runbooks.",
    accent: "emerald",
    stat: { label: "SOC drills run", values: [0.4, 0.6, 0.5, 0.78, 0.7, 0.9] },
  },
  {
    code: "CAP-04",
    icon: Cloud,
    title: "Cloud-Native Labs",
    sub: "Azure · AWS · GCP",
    desc:
      "Hands-on environments for cloud workload defence, identity hardening, and zero-trust enforcement.",
    accent: "cyan",
    stat: { label: "Lab environments", values: [0.5, 0.55, 0.7, 0.6, 0.85, 0.92] },
  },
  {
    code: "CAP-05",
    icon: AlertOctagon,
    title: "Security Simulations",
    sub: "MITRE ATT&CK-aligned",
    desc:
      "Adversary emulation, purple-team exercises and breach simulations mapped to tactics & techniques.",
    accent: "amber",
    stat: { label: "ATT&CK coverage", values: [0.35, 0.55, 0.7, 0.8, 0.88, 0.94] },
  },
  {
    code: "CAP-06",
    icon: Layers,
    title: "Microsoft Ecosystem Expertise",
    sub: "Entra · Sentinel · Defender · Purview",
    desc:
      "Deep coverage of the Microsoft Security stack with active certification & field engagements.",
    accent: "indigo",
    stat: { label: "Microsoft stack depth", values: [0.6, 0.7, 0.8, 0.85, 0.9, 0.97] },
  },
];

const accentMap = {
  sky:     { text: "text-sky-300",     ring: "rgba(56,189,248,0.35)",  bar: "#38BDF8" },
  blue:    { text: "text-blue-300",    ring: "rgba(96,165,250,0.35)",  bar: "#60A5FA" },
  emerald: { text: "text-emerald-300", ring: "rgba(16,185,129,0.35)",  bar: "#10B981" },
  cyan:    { text: "text-cyan-300",    ring: "rgba(34,211,238,0.35)",  bar: "#22D3EE" },
  amber:   { text: "text-amber-300",   ring: "rgba(251,191,36,0.35)",  bar: "#FBBF24" },
  indigo:  { text: "text-indigo-300",  ring: "rgba(129,140,248,0.35)", bar: "#818CF8" },
};

const HEADLINE_STATS = [
  { kpi: "26+", label: "Global cohorts delivered" },
  { kpi: "14", label: "Time zones covered" },
  { kpi: "4", label: "Continents reached" },
  { kpi: "99.4%", label: "MITRE ATT&CK lab coverage" },
];

const REGIONS = [
  "North America",
  "Europe",
  "Middle East",
  "South Asia",
  "South-East Asia",
  "Australia & NZ",
];

/* ---------------- Component ---------------- */
export default function GlobalDelivery() {
  return (
    <section
      id="delivery"
      data-testid="global-delivery-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Ambient backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          background:
            "radial-gradient(55% 50% at 80% 10%, rgba(56,189,248,0.10) 0%, transparent 60%), radial-gradient(45% 40% at 10% 90%, rgba(99,102,241,0.08) 0%, transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-white/30" />
              <span className="label-eyebrow">07 — Global Delivery</span>
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight text-white leading-[1.05]">
              Engineered for global,{" "}
              <span className="text-slate-500">regulated enterprises.</span>
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed max-w-xl">
              From SOC modernization in the Gulf to Microsoft security
              enablement across APAC and the Americas — programs are designed,
              delivered, and supported with enterprise-grade rigour.
            </p>
          </div>

          {/* Headline KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 min-w-[280px]">
            {HEADLINE_STATS.map((s) => (
              <div
                key={s.label}
                data-testid={`delivery-kpi-${s.label
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
              >
                <div className="font-display text-2xl text-white tracking-tight">
                  {s.kpi}
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-slate-500 mt-1 leading-snug">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Capability grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAPABILITIES.map((c, i) => (
            <CapabilityCard key={c.code} cap={c} index={i} />
          ))}
        </div>

        {/* Regions strip */}
        <div className="mt-14 rounded-2xl border border-white/10 bg-[#0A0C13]/80 backdrop-blur p-6 md:p-7">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-center gap-3">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-60 animate-ping" />
                <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-300/90">
                Active delivery footprint
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {REGIONS.map((r) => (
                <span
                  key={r}
                  data-testid={`delivery-region-${r
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 text-xs text-slate-300"
                >
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  {r}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Capability card ---------------- */
function CapabilityCard({ cap, index }) {
  const Icon = cap.icon;
  const a = accentMap[cap.accent];
  const max = Math.max(...cap.stat.values);

  return (
    <motion.article
      data-testid={`delivery-card-${cap.code.toLowerCase()}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: index * 0.05 }}
      className="group relative rounded-2xl border border-white/10 bg-[#0A0C13]/85 backdrop-blur p-6 hover:border-white/20 transition-colors overflow-hidden"
    >
      {/* Hover glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(60% 80% at 0% 0%, ${a.ring} 0%, transparent 60%)`,
        }}
      />

      {/* Code label */}
      <div className="flex items-center justify-between relative">
        <div
          className={`h-10 w-10 rounded-lg flex items-center justify-center border border-white/10 bg-black/30 ${a.text}`}
        >
          <Icon size={17} />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
          {cap.code}
        </span>
      </div>

      {/* Title + sub */}
      <h3 className="relative mt-5 text-white text-lg font-semibold leading-snug">
        {cap.title}
      </h3>
      <div className={`relative mt-1 text-xs ${a.text} font-mono tracking-wide`}>
        {cap.sub}
      </div>

      {/* Description */}
      <p className="relative mt-3 text-sm text-slate-400 leading-relaxed">
        {cap.desc}
      </p>

      {/* Stat micro-viz */}
      <div className="relative mt-6 pt-5 border-t border-white/5">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            {cap.stat.label}
          </span>
          <span className="font-mono text-[10px] text-slate-400">
            {Math.round(max * 100)}%
          </span>
        </div>
        <div className="flex items-end gap-1.5 h-9">
          {cap.stat.values.map((v, idx) => (
            <motion.span
              key={idx}
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 + idx * 0.06 }}
              className="flex-1 origin-bottom rounded-sm"
              style={{
                height: `${v * 100}%`,
                background: `linear-gradient(180deg, ${a.bar} 0%, ${a.bar}55 100%)`,
                boxShadow: `0 0 12px ${a.bar}33`,
              }}
            />
          ))}
        </div>
      </div>
    </motion.article>
  );
}
